import { createStore, applyMiddleware, compose } from 'redux';
import createSagaMiddleware from 'redux-saga';
import type { History } from 'history';
import { fromJS } from 'immutable';
import { routerMiddleware } from 'connected-react-router/immutable';
import { responsiveStoreEnhancer } from 'redux-responsive';

import { getViewInitialState } from 'src/store/view/reducer';
import { HOTEL_MOBILE_PAGES } from 'src/routes/HotelReviewsPage/constants';
import { isSSR } from 'src/helpers/ssr';
import { isWebGlSupported } from 'src/utils/mapUtils';
import { convertServerDateToClientDate } from 'src/utils/dateUtils';
import { getNightsFromObject } from 'src/utils/searchParamsUtils';
import { searchParamsDTO } from 'src/store/enqueue/enqueueDTO';
import sagas from './sagas';
import reducer from './reducers';
import { SEARCHER_STATE } from './constants/searcher';

// eslint-disable-next-line no-underscore-dangle
const composeEnhancers =
  (!isSSR && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;
const sagaMiddleware = createSagaMiddleware();
const middlewares = [sagaMiddleware];
const getStore = (props, history = '' as unknown as History<unknown>) => {
  const {
    logo,
    hotel,
    isMobile,
    isWebview,
    airports,
    breadcrumbs,
    search_params,
    ratings,
    experiences,
    images,
    reviews,
    video,
    operators,
    extraItems,
    requestId,
    departures,
    offerParams,
    platform,
    availableSearchTypes,
    mobile_app_room_offers,
  } = props;

  const startDate = offerParams.start_date || search_params.start_date;
  const departure = search_params.from.city;
  const carouselDate = startDate;
  const searchParams = {
    startDate: convertServerDateToClientDate(startDate), // поиск
    adults: search_params.adults,
    kidsAges: search_params.kids.ages,
    requestId,
    from: search_params.from,
    to: search_params.to,
    nights: offerParams.nights || getNightsFromObject(search_params.nights),
  };

  const preloadedState = {
    logo,
    view: {
      isMobile,
      isWebview,
      isShortInfoClamped: true,
      isWidgetShown: true,
      isWebGlSupported: isWebGlSupported(),
      platform,
      searchType: search_params.search_type,
      availableSearchTypes: availableSearchTypes || undefined,
      page:
        mobile_app_room_offers === 'true' ? HOTEL_MOBILE_PAGES.SEARCHER : null,
      ...getViewInitialState(),
    },
    breadcrumbs,
    hotel,
    airports,
    ratings,
    reviews: {
      fetching: false,
      isLoadingNew: false,
      visibleCount: 10,
      // для старого типа отзывов
      data: [],
      // для новых отзывов
      reviews: [],
      pagination: {
        page: 1,
        total: isSSR ? reviews.length : 0,
      },
      sorting: {
        order: 'desc',
        field: 'date',
      },
    },
    departures,
    searcher: {
      ...searchParams,
      state: SEARCHER_STATE.INITIAL,
      operatorsFilter: {},
      filters: {},
      carouselDate: convertServerDateToClientDate(carouselDate), // карусель
      departure,
    },
    enqueue: {
      request_id: typeof requestId === 'string' ? requestId : null,
      search_params: search_params ? searchParamsDTO(search_params) : null,
    },
    searchOffer: offerParams,
    operators,
    images,
    video,
    extraItems,
    experiences,
  };

  const store = createStore(
    reducer,
    fromJS(preloadedState),
    composeEnhancers(
      responsiveStoreEnhancer,
      applyMiddleware(...middlewares, routerMiddleware(history)),
    ),
  );
  sagaMiddleware.run(sagas);
  return store;
};

export default getStore;
